"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TaxRateService = void 0;
const decimal_js_1 = require("decimal.js");
const TaxModels_1 = require("./TaxModels");
const BlazeInvalidArgException_1 = require("../exceptions/BlazeInvalidArgException");
const CollectionUtils_1 = require("../utils/CollectionUtils");
const _1 = require(".");
/**
 * this is a converted version of the TaxRateService from Java to TypeScript.
 * original code: https://github.com/GetGreenline/420connect-platform/blob/main/java/420connect/connect-core/src/main/java/com/fourtwenty/core/services/mgmt/impl/TaxServiceImpl.java
 */
class TaxRateService {
    constructor() {
        this.systemFeeCategoryId = undefined;
    }
    processTaxRates(taxRateContext, taxRates) {
        const itemResponses = this.computeItemTaxes(taxRateContext, taxRates);
        const aggregatedDetails = this.aggregateTaxDetails(itemResponses);
        let totalAppliedTaxAmount = new decimal_js_1.Decimal(0);
        let totalPreTaxAmount = new decimal_js_1.Decimal(0);
        let totalPostTaxAmount = new decimal_js_1.Decimal(0);
        for (const detail of aggregatedDetails) {
            const amt = detail.appliedTaxAmount;
            totalAppliedTaxAmount = totalAppliedTaxAmount.add(amt);
            if (detail.preTax) {
                totalPreTaxAmount = totalPreTaxAmount.add(amt);
            }
            else {
                totalPostTaxAmount = totalPostTaxAmount.add(amt);
            }
        }
        return {
            companyId: taxRateContext.companyId,
            locationId: taxRateContext.locationId,
            appliedTaxAmount: totalAppliedTaxAmount,
            totalPreTaxAmount,
            totalPostTaxAmount,
            taxDetails: aggregatedDetails,
            items: itemResponses,
        };
    }
    processTaxRatesInCents(taxRateContext, taxRates) {
        const appliedTaxRateRes = this.processTaxRates(taxRateContext, taxRates);
        return {
            companyId: appliedTaxRateRes.companyId,
            locationId: appliedTaxRateRes.locationId,
            appliedTaxAmount: this.convertDollarToCent(appliedTaxRateRes.appliedTaxAmount),
            totalPreTaxAmount: this.convertDollarToCent(appliedTaxRateRes.totalPreTaxAmount),
            totalPostTaxAmount: this.convertDollarToCent(appliedTaxRateRes.totalPostTaxAmount),
            taxDetails: appliedTaxRateRes.taxDetails.map(detail => ({
                ...detail,
                appliedTaxAmount: this.convertDollarToCent(detail.appliedTaxAmount),
            })),
            items: appliedTaxRateRes.items.map(item => ({
                ...item,
                taxDetails: item.taxDetails.map(detail => ({
                    ...detail,
                    appliedTaxAmount: this.convertDollarToCent(detail.appliedTaxAmount),
                })),
                appliedTaxAmount: this.convertDollarToCent(item.appliedTaxAmount),
            })),
        };
    }
    setSystemFeeCategoryId(categoryId) {
        this.systemFeeCategoryId = categoryId;
    }
    convertDollarToCent(amount) {
        return amount.mul(100).toDecimalPlaces(0, decimal_js_1.Decimal.ROUND_HALF_UP).toNumber();
    }
    aggregateTaxDetails(itemResponses) {
        const taxDetailsById = new Map();
        for (const itemResponse of itemResponses) {
            for (const detail of itemResponse.taxDetails) {
                let aggregatedDetail = taxDetailsById.get(detail.id);
                if (!aggregatedDetail) {
                    aggregatedDetail = {
                        id: detail.id,
                        taxName: detail.taxName,
                        taxType: detail.taxType,
                        preTax: detail.preTax,
                        appliedTaxAmount: new decimal_js_1.Decimal(0),
                    };
                    taxDetailsById.set(detail.id, aggregatedDetail);
                }
                aggregatedDetail.appliedTaxAmount = aggregatedDetail.appliedTaxAmount.add(detail.appliedTaxAmount);
            }
        }
        // Round to 2 decimal places
        for (const detail of taxDetailsById.values()) {
            detail.appliedTaxAmount = detail.appliedTaxAmount.toDecimalPlaces(2, decimal_js_1.Decimal.ROUND_HALF_UP);
        }
        return Array.from(taxDetailsById.values());
    }
    computeItemTaxes(ctx, allRates) {
        if (CollectionUtils_1.CollectionUtils.isNullOrEmpty(ctx.itemContextList)) {
            return [];
        }
        const topo = this.topoSortByCompoundDependencies(allRates);
        const preTaxRates = topo.filter(rate => rate.preTax);
        const postTaxRates = topo.filter(rate => !rate.preTax);
        const responses = [];
        for (const item of ctx.itemContextList) {
            const taxAmountsById = new Map();
            const netSubTotal = item.netSubTotal;
            const pre = this.applyPreTaxSegment(preTaxRates, netSubTotal, item, ctx, taxAmountsById);
            const post = this.applyPostTaxSegment(postTaxRates, netSubTotal.sub(pre.total), item, ctx, taxAmountsById);
            const allDetails = [...pre.details, ...post.details];
            const isTaxExemptableProduct = this.isTaxExemptableProduct(ctx, item, allRates);
            responses.push({
                itemId: item.cartItemId,
                product: {
                    productId: item.productId,
                    isTaxExemptableProduct,
                },
                appliedTaxAmount: pre.total.add(post.total),
                taxDetails: allDetails,
            });
        }
        return responses;
    }
    isTaxExemptableProduct(ctx, item, allRates) {
        const applicableTaxRates = allRates.filter(rate => ctx.isApplicableToLocation(rate));
        const isTaxExemptableProduct = applicableTaxRates.length > 0
            ? allRates.every((rate) => item.isTaxExempt(rate))
            : false;
        return isTaxExemptableProduct;
    }
    applyPreTaxSegment(ratesTopological, shopperPriceInclusive, itemCtx, ctx, taxAmtById) {
        if (ratesTopological.length === 0) {
            return { total: new decimal_js_1.Decimal(0), details: [] };
        }
        const ratesById = this.toRateMap(ratesTopological);
        const pctRates = this.filterPercentageRates(ratesTopological);
        const absRates = this.filterAbsoluteRates(ratesTopological);
        const details = [];
        const absTotal = this.applyFlatAndPotencyTaxes(absRates, itemCtx, ctx, taxAmtById, details);
        const effectiveRateMap = this.buildEffectiveRateMap(pctRates, itemCtx, ctx, ratesById);
        const totalEffectivePct = this.getTotalEffectivePct(effectiveRateMap);
        const netSubtotal = this.computeNetSubtotal(shopperPriceInclusive, absTotal, totalEffectivePct);
        const pctTotal = this.applyPercentageTaxes(pctRates, netSubtotal, itemCtx, ctx, taxAmtById, effectiveRateMap, details);
        const totalTax = absTotal.add(pctTotal);
        return { total: totalTax, details };
    }
    applyPostTaxSegment(ratesTopological, baseExclusive, itemCtx, ctx, taxAmtById) {
        let segmentTotal = new decimal_js_1.Decimal(0);
        const details = [];
        for (const rate of ratesTopological) {
            if (!rate.isActive || !this.isTaxRateApplicable(rate, itemCtx, ctx)) {
                taxAmtById.set(rate.id, new decimal_js_1.Decimal(0));
                continue;
            }
            const baseForRate = this.percentageBase(baseExclusive, rate, taxAmtById);
            const amount = this.calculateTaxRateAmount(rate, itemCtx, ctx, baseForRate);
            taxAmtById.set(rate.id, amount);
            segmentTotal = segmentTotal.add(amount);
            details.push(this.buildDetail(rate, amount, false));
        }
        return { total: segmentTotal, details };
    }
    toRateMap(rates) {
        return new Map(rates.map(r => [r.id, r]));
    }
    filterPercentageRates(rates) {
        return rates.filter(r => r.rateType === TaxModels_1.RateType.PERCENTAGE);
    }
    filterAbsoluteRates(rates) {
        return rates.filter(r => r.rateType !== TaxModels_1.RateType.PERCENTAGE);
    }
    applyFlatAndPotencyTaxes(rates, itemCtx, ctx, taxAmtById, details) {
        let total = new decimal_js_1.Decimal(0);
        for (const rate of rates) {
            const applicable = rate.isActive && this.isTaxRateApplicable(rate, itemCtx, ctx);
            const amount = applicable
                ? this.calculateTaxRateAmount(rate, itemCtx, ctx, new decimal_js_1.Decimal(0))
                : new decimal_js_1.Decimal(0);
            taxAmtById.set(rate.id, amount);
            total = total.add(amount);
            if (applicable) {
                details.push(this.buildDetail(rate, amount, true));
            }
        }
        return total;
    }
    buildEffectiveRateMap(pctRates, itemCtx, ctx, ratesById) {
        const effRateMap = new Map();
        for (const rate of pctRates) {
            if (!rate.isActive || !this.isTaxRateApplicable(rate, itemCtx, ctx)) {
                effRateMap.set(rate.id, new decimal_js_1.Decimal(0));
                continue;
            }
            const base = this.findTaxRateAmount(rate, itemCtx, ctx).div(100);
            const compounded = this.computeEffectivePct(rate, base, effRateMap, ratesById);
            effRateMap.set(rate.id, compounded);
        }
        return effRateMap;
    }
    computeEffectivePct(rate, baseRate, effRateMap, ratesById) {
        var _a;
        const parents = rate.compoundOnTaxConfigIds || [];
        if (parents.length === 0)
            return baseRate;
        let totalParentEffectiveRate = new decimal_js_1.Decimal(0);
        for (const parentId of parents) {
            const parentEffectiveRate = effRateMap.get(parentId);
            if (parentEffectiveRate === undefined) {
                const displayName = ((_a = ratesById.get(parentId)) === null || _a === void 0 ? void 0 : _a.getEffectiveDisplayName()) || 'a parent tax';
                throw new BlazeInvalidArgException_1.BlazeInvalidArgException(TaxRateService.TAX_RATE, `Missing effective rate for ${displayName}`);
            }
            totalParentEffectiveRate = totalParentEffectiveRate.add(parentEffectiveRate);
        }
        /**
         * example
         * - 2 rates are present:
         * 1. taxA
         * 2. taxB (compounds on taxA)
         *
         * effective rate of taxB is:
         * (subtotal + (subtotal * taxA)) * taxB                        where (subtotal * taxA) is the amount of taxA
            = (subtotal * taxB) + (subtotal * taxA * taxB)
            = (subtotal * taxB) + (subtotal * taxB * taxA)
            = ((subtotal * taxB) * 1) + ((subtotal * taxB) * taxA)
            = (subtotal * taxB) * (1 + taxA)
            = subtotal * (taxB * (1 + taxA))                            where effectiveRateOfTaxB = taxB * (1 + taxA)
         */
        return baseRate.mul(new decimal_js_1.Decimal(1).add(totalParentEffectiveRate));
    }
    getTotalEffectivePct(effRateMap) {
        return Array.from(effRateMap.values()).reduce((acc, val) => acc.add(val), new decimal_js_1.Decimal(0));
    }
    computeNetSubtotal(priceInclusive, absTax, totalEffPct) {
        return priceInclusive.sub(absTax).div(new decimal_js_1.Decimal(1).add(totalEffPct));
    }
    applyPercentageTaxes(rates, netSubtotal, itemCtx, ctx, taxAmtById, effRateMap, details) {
        let total = new decimal_js_1.Decimal(0);
        for (const rate of rates) {
            if (!rate.isActive || !this.isTaxRateApplicable(rate, itemCtx, ctx))
                continue;
            const effectivePct = effRateMap.get(rate.id);
            const amount = netSubtotal.mul(effectivePct).toDecimalPlaces(2, decimal_js_1.Decimal.ROUND_HALF_UP);
            taxAmtById.set(rate.id, amount);
            total = total.add(amount);
            details.push(this.buildDetail(rate, amount, true));
        }
        return total;
    }
    buildDetail(rate, amount, isPreTax) {
        return {
            id: rate.id,
            taxName: rate.getEffectiveDisplayName(),
            taxType: rate.taxType,
            preTax: isPreTax,
            appliedTaxAmount: amount,
        };
    }
    percentageBase(netSubtotal, taxRate, taxAmountsById) {
        if (taxRate.rateType !== TaxModels_1.RateType.PERCENTAGE || CollectionUtils_1.CollectionUtils.isNullOrEmpty(taxRate.compoundOnTaxConfigIds)) {
            return netSubtotal;
        }
        let base = netSubtotal;
        for (const parentId of taxRate.compoundOnTaxConfigIds) {
            const parentAmount = taxAmountsById.get(parentId);
            if (parentAmount === undefined) {
                throw new Error(`TaxConfig ${taxRate.id} references unknown parent ID '${parentId}'`);
            }
            base = base.add(parentAmount);
        }
        return base;
    }
    /**
     * Topologically sorts the tax rates so each appears
     * after any rate it depends on.
     * <p>
     * Uses the standard indegree-based approach:
     * <a href="https://www.geeksforgeeks.org/dsa/topological-sorting-indegree-based-solution/">...</a>
     */
    topoSortByCompoundDependencies(all) {
        var _a, _b;
        const byId = new Map();
        for (const tr of all) {
            if (byId.has(tr.id)) {
                throw new BlazeInvalidArgException_1.BlazeInvalidArgException(TaxRateService.TAX_RATE, `Duplicate TaxConfig ID: ${tr.id}`);
            }
            byId.set(tr.id, tr);
        }
        // higher indegree -> parent tax rates
        // lower indegree -> child tax rates
        const indeg = new Map();
        /**
         * key: parent tax rate ID
         * value: list of child tax rate IDs that compound on this parent
         */
        const graph = new Map();
        all.forEach(tr => {
            indeg.set(tr.id, 0);
            graph.set(tr.id, []);
        });
        for (const tr of all) {
            const deps = tr.compoundOnTaxConfigIds || [];
            for (const parentId of deps) {
                const parent = byId.get(parentId);
                if (!parent) {
                    throw new BlazeInvalidArgException_1.BlazeInvalidArgException(TaxRateService.TAX_RATE, `TaxConfig ${tr.id} references unknown parent ID: ${parentId}`);
                }
                if (tr.preTax && !parent.preTax) {
                    throw new BlazeInvalidArgException_1.BlazeInvalidArgException(TaxRateService.TAX_RATE, `Pre-tax TaxConfig ${tr.name} cannot compound on post-tax tax: ${parent.getEffectiveDisplayName()}`);
                }
                (_a = graph.get(parentId)) === null || _a === void 0 ? void 0 : _a.push(tr.id);
                indeg.set(tr.id, ((_b = indeg.get(tr.id)) !== null && _b !== void 0 ? _b : 0) + 1);
            }
        }
        const q = [];
        indeg.forEach((d, id) => {
            if (d === 0)
                q.push(id);
        });
        const ordered = [];
        while (q.length > 0) {
            const id = q.shift();
            ordered.push(byId.get(id));
            for (const nbr of graph.get(id)) {
                const newIndeg = indeg.get(nbr) - 1;
                indeg.set(nbr, newIndeg);
                if (newIndeg === 0)
                    q.push(nbr);
            }
        }
        if (ordered.length !== all.length) {
            throw new BlazeInvalidArgException_1.BlazeInvalidArgException(TaxRateService.TAX_RATE, 'Circular dependency detected in tax compounding configuration. ' +
                'Please ensure that tax rates do not reference each other in a loop.');
        }
        return ordered;
    }
    isTaxRateApplicable(taxRate, itemContext, taxRateContext) {
        const taxIsExempt = this.isTaxExemptByContext(taxRate, [
            itemContext,
            taxRateContext
        ]);
        const isCategoryIdMatch = this.isApplicableByType(itemContext, taxRateContext, taxRate.categoryRates);
        const isCannabisTypeMatch = this.isApplicableByType(itemContext, taxRateContext, taxRate.cannabisTypeRates);
        const isConsumerTypeMatch = this.isApplicableByType(itemContext, taxRateContext, taxRate.consumerTypeRates);
        return !taxIsExempt && isCategoryIdMatch && isCannabisTypeMatch && isConsumerTypeMatch;
    }
    isTaxExemptByContext(taxRate, contexts) {
        return contexts.some(context => context.isTaxExempt(taxRate));
    }
    isApplicableByType(itemContext, taxRateContext, taxRules) {
        if (CollectionUtils_1.CollectionUtils.isNullOrEmpty(taxRules))
            return true;
        return taxRules.some(rule => {
            if (rule instanceof _1.CategoryRate) {
                return rule.matches(taxRateContext, itemContext, this.systemFeeCategoryId);
            }
            return rule.matches(taxRateContext, itemContext);
        });
    }
    calculateTaxRateAmount(taxRate, itemContext, taxRateContext, baseAmount) {
        const taxRateAmount = this.findTaxRateAmount(taxRate, itemContext, taxRateContext);
        let amount = new decimal_js_1.Decimal(baseAmount);
        if (taxRate.taxableFees.includes(TaxModels_1.TaxableFees.DEPOSIT_FEE)) {
            amount = amount.add(itemContext.netDepositFee);
        }
        switch (taxRate.rateType) {
            case TaxModels_1.RateType.POTENCY:
                return this.calculatePotencyTaxRateAmount(taxRate, itemContext, taxRateAmount);
            case TaxModels_1.RateType.FLAT:
                return taxRateAmount.mul(itemContext.quantity).toDecimalPlaces(4, decimal_js_1.Decimal.ROUND_HALF_UP);
            case TaxModels_1.RateType.PERCENTAGE:
                const percentage = taxRateAmount.div(100);
                if (taxRate.preTax) {
                    const divisor = new decimal_js_1.Decimal(1).add(percentage);
                    return amount.sub(amount.div(divisor)).toDecimalPlaces(2, decimal_js_1.Decimal.ROUND_HALF_UP);
                }
                return amount.mul(percentage).toDecimalPlaces(4, decimal_js_1.Decimal.ROUND_HALF_UP);
            default:
                return new decimal_js_1.Decimal(0);
        }
    }
    addRates(rules, rates) {
        if (CollectionUtils_1.CollectionUtils.isNullOrEmpty(rates))
            return;
        rules.push(...rates);
    }
    findTaxRateAmount(taxRate, itemContext, taxRateContext) {
        var _a;
        const rule = this.findTaxRule(taxRate, itemContext, taxRateContext);
        const amount = (_a = rule === null || rule === void 0 ? void 0 : rule.getRateAmount()) !== null && _a !== void 0 ? _a : taxRate.rateAmount;
        return amount !== null && amount !== void 0 ? amount : new decimal_js_1.Decimal(0);
    }
    findTaxRule(taxRate, itemContext, taxRateContext) {
        const rules = [];
        this.addRates(rules, taxRate.categoryRates);
        this.addRates(rules, taxRate.cannabisTypeRates);
        this.addRates(rules, taxRate.consumerTypeRates);
        return rules.find(rule => rule.matches(taxRateContext, itemContext)) || null;
    }
    calculatePotencyTaxRateAmount(taxRate, itemContext, taxRateAmount) {
        const thc = this.getEffectiveThc(taxRate, itemContext);
        if (thc.lte(0)) {
            return new decimal_js_1.Decimal(0);
        }
        const totalTaxRateAmount = taxRateAmount.mul(thc).mul(itemContext.quantity);
        const taxRateAmountPer100Mg = totalTaxRateAmount.div(100).toDecimalPlaces(4, decimal_js_1.Decimal.ROUND_HALF_UP);
        return taxRateAmountPer100Mg;
    }
    getEffectiveThc(taxRate, itemContext) {
        var _a, _b;
        const batchThc = ((_a = itemContext.batchPotency) === null || _a === void 0 ? void 0 : _a.thc) ? new decimal_js_1.Decimal(itemContext.batchPotency.thc) : new decimal_js_1.Decimal(0);
        if (batchThc.gt(0)) {
            return batchThc;
        }
        if (taxRate.useProductPotency) {
            const productThc = ((_b = itemContext.productPotency) === null || _b === void 0 ? void 0 : _b.thc) ? new decimal_js_1.Decimal(itemContext.productPotency.thc) : new decimal_js_1.Decimal(0);
            if (productThc.gt(0)) {
                return productThc;
            }
        }
        const isCannabisItem = itemContext.cannabisType ? this.isCannabis(itemContext.cannabisType) : false;
        if (isCannabisItem && taxRate.requirePotency) {
            throw new BlazeInvalidArgException_1.BlazeInvalidArgException(TaxRateService.TAX_RATE, `THC potency value is required for tax rate: ${taxRate.getEffectiveDisplayName()}`);
        }
        return new decimal_js_1.Decimal(0);
    }
    isCannabis(cannabisType) {
        if (!cannabisType)
            return false;
        return cannabisType !== TaxModels_1.CannabisType.CBD &&
            cannabisType !== TaxModels_1.CannabisType.NON_CANNABIS &&
            cannabisType !== TaxModels_1.CannabisType.DEFAULT;
    }
}
exports.TaxRateService = TaxRateService;
TaxRateService.TAX_RATE = 'TAX_RATE';
//# sourceMappingURL=TaxRateService.js.map